import { configureCompat, createApp, h } from 'vue'
import App from './App.vue'
import router from '@/router'
import store from '@/store'
import filtersPlugin from '@/filters'
import Bugsnag from '@/plugins/bugsnag'
import '@/utils/fix-viewport-height'
import vuetify from '@/plugins/vuetify'
import '@/styles/_vuetify-legacy.scss'
import { setDefaultUrl } from '@/utils/axios'
import { settings } from '@/utils/settings'
import { installAppPlugins } from '@/plugins/app-plugins'

// Store used within the axios setup file, hence why this needs to be called
// after app bootstrap rather than in main.js.
setDefaultUrl(settings.API_URL)

if (typeof configureCompat === 'function') {
  configureCompat({
    ATTR_ENUMERATED_COERCION: 'suppress-warning',
    ATTR_FALSE_VALUE: 'suppress-warning',
    COMPONENT_ASYNC: false,
    COMPONENT_FUNCTIONAL: false,
    COMPONENT_V_MODEL: 'suppress-warning',
    INSTANCE_LISTENERS: false,
    PRIVATE_APIS: 'suppress-warning'
  })
}

function readRefValue(value) {
  return value && typeof value === 'object' && 'value' in value
    ? value.value
    : value
}

// Reads width from Vuetify's own reactive `display.width` ref (kept in
// sync via Vuetify's internal window resize listener) rather than calling
// `window.innerWidth` directly. A plain `window.innerWidth` read has no Vue
// reactivity tracking, so every `$vuetifyDisplay.*` getter below would only
// ever reflect the viewport size at the component's last unrelated
// re-render - never updating on an actual browser resize (devtools
// responsive testing, window resize/maximize, orientation change).
function createVuetifyDisplayCompat(vuetifyInstance) {
  const display = vuetifyInstance.display || {}
  const width = () => Number(readRefValue(display.width)) || 0
  const hasWidth = () => width() > 0
  const below = (value) => hasWidth() && width() < value
  const atLeast = (value) => hasWidth() && width() >= value

  return {
    get xs() {
      return hasWidth() ? below(600) : !!readRefValue(display.xs)
    },
    get sm() {
      return hasWidth()
        ? atLeast(600) && below(960)
        : !!readRefValue(display.sm)
    },
    get md() {
      return hasWidth()
        ? atLeast(960) && below(1264)
        : !!readRefValue(display.md)
    },
    get lg() {
      return hasWidth()
        ? atLeast(1264) && below(1904)
        : !!readRefValue(display.lg)
    },
    get xl() {
      return hasWidth() ? atLeast(1904) : !!readRefValue(display.xl)
    },
    get xxl() {
      return !!readRefValue(display.xxl)
    },
    get xsOnly() {
      return this.xs
    },
    get xlOnly() {
      return this.xl
    },
    get smAndDown() {
      return hasWidth() ? below(960) : !!readRefValue(display.smAndDown)
    },
    get smAndUp() {
      return hasWidth() ? atLeast(600) : !!readRefValue(display.smAndUp)
    },
    get mdAndDown() {
      return hasWidth() ? below(1264) : !!readRefValue(display.mdAndDown)
    },
    get mdAndUp() {
      return hasWidth() ? atLeast(960) : !!readRefValue(display.mdAndUp)
    },
    get lgAndDown() {
      return hasWidth() ? below(1904) : !!readRefValue(display.lgAndDown)
    },
    get lgAndUp() {
      return hasWidth() ? atLeast(1264) : !!readRefValue(display.lgAndUp)
    },
    get xlAndDown() {
      return hasWidth() ? true : !!readRefValue(display.xlAndDown)
    },
    get xlAndUp() {
      return this.xl
    },
    get mobile() {
      return hasWidth() ? below(960) : !!readRefValue(display.mobile)
    },
    get width() {
      return width()
    },
    get height() {
      return readRefValue(display.height)
    },
    get name() {
      return readRefValue(display.name)
    }
  }
}

export function createRootApp() {
  const app = createApp({
    name: 'RootApp',
    compatConfig: {
      RENDER_FUNCTION: false
    },
    render: () => h(App)
  })

  Bugsnag.install(app)
  app.use(vuetify)
  app.config.globalProperties.$vuetifyDisplay =
    createVuetifyDisplayCompat(vuetify)
  installAppPlugins(app)
  app.use(filtersPlugin)
  app.use(router)
  app.use(store)

  return app
}

createRootApp().mount('#app')
